import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { ReactNode, useMemo, useState } from "react";
import { TaxFiling, useGetFilingDetailsQuery } from "store/apis/filing";
import { Status } from "./Status";
import ToolTip from "components/design/toolTip";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import { MONTHLY } from "constants/salesTaxRegistrationStatusType";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { ConditionalRender } from "./SalesTaxTracker";
import { useQuery } from "hooks/useQuery";
import { TASK_TYPE } from "store/apis/taskTrackerSalesTax";
import { useGetSalesTaxRegistrationDetailsQuery } from "store/apis/salesTaxRegistration";
import {
  redirectToFiling,
  redirectToRegistration,
  statusMap,
} from "./SalesTaxTrackerTable";
import { RegistrationAccordion } from "./RegistrationAccordion";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { ChatPane } from "components/ChatPane/ChatPane";
import {
  PreparerColumn,
  ServiceTeamColumn,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { SALES_TAX } from "constants/apps";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { openLink } from "utils/openLink";
import { Button } from "components/DesignSystem/Button/Button";

const paymentScheduleMap = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  SEMI_ANNUALLY: "Semi annually",
  ANNUALLY: "Annually",
};

export const DetailItem = ({
  children,
  label,
  toolTipText,
}: {
  children: ReactNode;
  label: string;
  toolTipText?: string | ReactNode;
}) => {
  return (
    <div className="t-flex t-items-start t-flex-col t-gap-1">
      <div className="t-flex t-items-center t-gap-x-1">
        <p className="t-text-body-sm t-text-text-30 t-m-0">{label}</p>
        {toolTipText && (
          <ToolTip text={toolTipText}>
            <span>
              <ToolTipIcon className="t-mb-1" />
            </span>
          </ToolTip>
        )}
      </div>

      {children}
    </div>
  );
};

const FilingDetails = ({ data }: { data?: TaxFiling }) => {
  return (
    <Accordion.Item
      value="DETAILS"
      className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
    >
      <Accordion.Trigger>
        <p className="t-text-subtext t-text-text-100 t-m-0">Filing details</p>
      </Accordion.Trigger>
      <Accordion.Content>
        <div className="t-grid t-grid-cols-2 t-justify-between t-gap-6">
          <DetailItem label="Total sales">
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                condition={Boolean(data?.total_sales)}
                fallback="-"
              >
                <AmountSuperScript amount={data?.total_sales!} />
              </ConditionalRender>
            </span>
          </DetailItem>

          <DetailItem label="Total tax collected">
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                condition={Boolean(data?.total_tax_collected)}
                fallback="-"
              >
                <AmountSuperScript amount={data?.total_tax_collected!} />
              </ConditionalRender>
            </span>
          </DetailItem>

          <DetailItem label="Total transactions">
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                fallback="-"
                condition={Boolean(data?.invoice_count)}
              >
                <AmountSuperScript amount={data?.invoice_count!} />
              </ConditionalRender>
            </span>
          </DetailItem>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

const About = ({
  data,
  groupId,
  entityId,
}: {
  data: TaxFiling;
  groupId: string;
  entityId: string;
}) => {
  const taskData = {
    uuid: data?.uuid!,
    assigned_team: data?.service_team,
    assigned_to: {
      uuid: data?.preparer?.user_service_team_map_id,
      name: data?.preparer?.name,
    },
  };

  return (
    <Accordion.Item
      value="ABOUT"
      className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
    >
      <Accordion.Trigger>
        <p className="t-text-subtext t-text-text-100 t-m-0">About</p>
      </Accordion.Trigger>
      <Accordion.Content>
        <div className="t-grid t-grid-cols-2 t-justify-between t-gap-6">
          <DetailItem label="Task Type">
            <div
              className="t-cursor-pointer"
              onClick={() =>
                redirectToFiling({
                  groupId: groupId!,
                  entityId: entityId!,
                  filingId: data?.uuid!,
                })
              }
            >
              <span className="t-text-subtext t-text-purple hover:t-underline">
                Sales Tax Filing
              </span>
            </div>
          </DetailItem>
          <DetailItem label="Task Status">
            <Status status={data?.status}>
              {statusMap[data?.status as keyof typeof statusMap]}
            </Status>
          </DetailItem>
          <DetailItem label="State">
            <span className="t-text-subtext t-text-text-60">{data?.state}</span>
          </DetailItem>
          <DetailItem label="Pricing">
            <span className="t-text-subtext t-text-text-60">
              <AmountSuperScript amount={75} />
            </span>
          </DetailItem>

          <DetailItem label="Period">
            <span className="t-text-subtext t-text-text-60">
              {data?.payment_schedule !== MONTHLY && (
                <span>
                  {dayjs(data?.filing_period_start_date).format("MMM")} -{" "}
                </span>
              )}
              {dayjs(data?.filing_period_end_date).format("MMM YYYY")}{" "}
              <span className="t-text-neutral-20">|</span>{" "}
              {data?.payment_schedule &&
                paymentScheduleMap[
                  data?.payment_schedule as keyof typeof paymentScheduleMap
                ]}
            </span>
          </DetailItem>

          <DetailItem label="Deadline">
            <span className="t-text-subtext t-text-text-60">
              {dayjs(data?.deadline).format(DISPLAY_DATE)}
            </span>
          </DetailItem>

          <DetailItem label="Service Team">
            <span className="t-text-subtext t-text-text-60 t-min-w-full">
              <ServiceTeamColumn tracker={SALES_TAX} taskData={taskData} />
            </span>
          </DetailItem>
          <DetailItem label="Preparer">
            <span className="t-text-subtext t-text-text-60 t-min-w-full">
              <PreparerColumn tracker={SALES_TAX} taskData={taskData} />
            </span>
          </DetailItem>

          <DetailItem
            label="Login Email ID"
            toolTipText={
              <span>
                Login email for accessing <br />
                Sidr account
              </span>
            }
          >
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                fallback="-"
                condition={Boolean(data?.login_email)}
              >
                {data?.login_email}
              </ConditionalRender>
            </span>
          </DetailItem>
          <DetailItem label="Assigned on">
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                fallback="-"
                condition={Boolean(data?.assigned_on)}
              >
                {dayjs(data?.assigned_on).format(DISPLAY_DATE)}
              </ConditionalRender>
            </span>
          </DetailItem>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

const FilingAccordion = ({
  data,
  groupId,
  entityId,
}: {
  data: TaxFiling;
  groupId: string;
  entityId: string;
}) => {
  return (
    <Accordion.Root
      className="t-w-full t-space-y-4"
      type="multiple"
      defaultValue={["ABOUT", "DETAILS"]}
    >
      <About data={data} groupId={groupId} entityId={entityId} />
      <FilingDetails data={data} />
    </Accordion.Root>
  );
};

export const SalesTaxTrackerSlider = ({
  isOpen,
  taskData,
  onClose,
  defaultSliderTab,
}: {
  isOpen: boolean;
  onClose: () => void;
  taskData: {
    entity: string;
    uuid: string;
    entityName?: string;
    groupName?: string;
    groupId: string;
    channelId?: string;
  };
  defaultSliderTab?: "DETAILS" | "CHAT";
}) => {
  const query = useQuery();
  const selectedTab = query.get("tab") || TASK_TYPE.FILINGS;

  const { data, isLoading, isSuccess, isFetching } = useGetFilingDetailsQuery(
    {
      fillingId: taskData?.uuid!,
      entityId: taskData?.entity!,
    },
    {
      skip:
        !taskData?.uuid ||
        !isOpen ||
        !taskData?.entity ||
        selectedTab !== TASK_TYPE.FILINGS,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: registrationDetailsData,
    isLoading: registrationLoading,
    isSuccess: registrationSuccess,
    isFetching: registrationFetching,
  } = useGetSalesTaxRegistrationDetailsQuery(
    {
      entityId: taskData?.entity!,
      state_registration_id: taskData?.uuid!,
    },
    {
      skip:
        !taskData?.uuid ||
        !isOpen ||
        !taskData?.entity ||
        selectedTab !== TASK_TYPE.REGISTRATIONS,
      refetchOnMountOrArgChange: true,
    }
  );

  const [channelId, setChannelId] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<"DETAILS" | "CHAT">("DETAILS");

  useMemo(() => {
    if (defaultSliderTab) {
      setCurrentTab(defaultSliderTab);
    }
  }, [defaultSliderTab]);

  useMemo(() => {
    if (taskData?.channelId) {
      setChannelId(taskData.channelId);
    }
  }, [taskData]);

  const { isForeignCA } = useRoleBasedView();

  const redirectToEntity = () => {
    const groupDetailsRoute = isForeignCA
      ? `/practice/crm/${taskData?.groupId}`
      : `/crm/${taskData?.groupId}`;

    openLink(groupDetailsRoute);
  };

  return (
    <Slider.Root open={isOpen} onOpenChange={onClose}>
      <Slider.Content width={600}>
        <Async.Root
          isEmpty={false}
          isLoading={
            isLoading ||
            isFetching ||
            registrationLoading ||
            registrationFetching
          }
          isSuccess={isSuccess || registrationSuccess}
        >
          <Async.Success>
            <Tab.Root
              value={currentTab}
              asChild
              onValueChange={(v) => setCurrentTab(v as typeof currentTab)}
            >
              <DashboardContainer className="t-h-full t-relative">
                <DashboardContainer.Header>
                  <Slider.Header
                    bottom={
                      <Tab.List className="!t-pt-0">
                        <Tab.Trigger value="DETAILS">
                          <span className="t-text-body-sm">Details</span>
                        </Tab.Trigger>
                        <Tab.Trigger value="CHAT">
                          <span className="t-text-body-sm ">Chat</span>
                        </Tab.Trigger>
                      </Tab.List>
                    }
                  >
                    <Slider.Title className="t-gap-x-2 t-items-center t-flex t-pb-4">
                      <div className="t-flex t-flex-col t-items-start">
                        <div
                          className="t-cursor-pointer"
                          onClick={redirectToEntity}
                        >
                          <span className="t-text-purple t-text-subtext hover:t-underline">
                            {taskData?.entityName}
                          </span>
                        </div>
                        <span className="t-text-text-30 t-text-body-sm">
                          {taskData?.groupName}
                        </span>
                      </div>
                    </Slider.Title>
                    <Slider.Close />
                  </Slider.Header>
                </DashboardContainer.Header>
                <DashboardContainer.Content className="t-h-full t-relative t-top-0">
                  <Tab.Content value="DETAILS" asChild>
                    <Slider.Body>
                      <ConditionalRender
                        condition={selectedTab === TASK_TYPE.FILINGS}
                        fallback={
                          <RegistrationAccordion
                            entityId={taskData?.entity}
                            groupId={taskData?.groupId}
                            data={registrationDetailsData}
                          />
                        }
                      >
                        <FilingAccordion
                          data={data!}
                          groupId={taskData?.groupId}
                          entityId={taskData?.entity}
                        />
                      </ConditionalRender>
                    </Slider.Body>
                  </Tab.Content>
                  <Tab.Content value="CHAT" asChild>
                    {channelId && (
                      <ChatPane
                        setChannelId={setChannelId}
                        channelId={channelId}
                      />
                    )}
                  </Tab.Content>
                </DashboardContainer.Content>
              </DashboardContainer>
            </Tab.Root>

            {currentTab === "DETAILS" && (
              <Slider.Footer className="t-sticky t-bottom-0">
                <div className=" t-flex t-flex- t-gap-x-2 t-justify-end ">
                  {selectedTab === TASK_TYPE.FILINGS && (
                    <Button
                      customType="primary"
                      size="small"
                      onClick={() => {
                        redirectToFiling({
                          groupId: taskData.groupId,
                          entityId: taskData.entity,
                          filingId: taskData.uuid,
                        });
                      }}
                    >
                      Go to filing
                    </Button>
                  )}
                  {selectedTab === TASK_TYPE.REGISTRATIONS && (
                    <Button
                      customType="primary"
                      onClick={() => {
                        redirectToRegistration({
                          groupId: taskData.groupId,
                          entityId: taskData.entity,
                          stateId: registrationDetailsData?.state_id,
                        });
                      }}
                      size="small"
                    >
                      Go to registration
                    </Button>
                  )}
                </div>
              </Slider.Footer>
            )}
          </Async.Success>
        </Async.Root>
      </Slider.Content>
    </Slider.Root>
  );
};
